/**
 * CABSectionSelector — Section picker for CAB generation.
 *
 * Lets users choose which sections to include in the blueprint before
 * starting generation. Displayed between the file list and the action bar.
 */

import React, { useCallback } from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

// ────────────────────────────────────────────────────────────
// Section definitions (order matches SECTION_ORDER on backend)
// ────────────────────────────────────────────────────────────

export interface CABSectionDef {
  key: string;
  label: string;
}

export const CAB_SECTIONS: CABSectionDef[] = [
  { key: 'projectIdentity', label: 'Project Identity' },
  { key: 'roles', label: 'Roles & Permissions' },
  { key: 'dataModel', label: 'Data Model' },
  { key: 'screens', label: 'Screens & UI' },
  { key: 'workflows', label: 'Workflows' },
  { key: 'reporting', label: 'Reporting & Analytics' },
  { key: 'integrations', label: 'System Integrations' },
  { key: 'rules', label: 'Business Rules' },
  { key: 'userStories', label: 'User Stories' },
  { key: 'suggestedQuestions', label: 'Suggested Questions' },
];

export const DEFAULT_ENABLED_SECTIONS = [
  'projectIdentity', 'roles', 'dataModel', 'screens',
  'workflows', 'reporting', 'integrations', 'rules',
  'suggestedQuestions',
];

// ────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────

interface CABSectionSelectorProps {
  enabledSections: string[];
  onChange: (sections: string[]) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

export const CABSectionSelector: React.FC<CABSectionSelectorProps> = ({
  enabledSections,
  onChange,
  disabled = false,
  readOnly = false,
}) => {
  const allSelected = CAB_SECTIONS.every((s) => enabledSections.includes(s.key));
  const selectedCount = enabledSections.length;
  const totalCount = CAB_SECTIONS.length;

  const handleToggleSection = useCallback(
    (key: string) => {
      if (disabled) return;
      const next = enabledSections.includes(key)
        ? enabledSections.filter((k) => k !== key)
        : [...enabledSections, key];
      onChange(next);
    },
    [enabledSections, onChange, disabled],
  );

  const handleSelectAll = useCallback(() => {
    if (disabled) return;
    if (allSelected) {
      onChange([]);
    } else {
      onChange(CAB_SECTIONS.map((s) => s.key));
    }
  }, [allSelected, onChange, disabled]);

  return (
    <div className="mt-bg-v2-lg border border-border-secondary rounded-bg-v2-md overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-bg-v2-md py-bg-v2-sm bg-bg-secondary">
        <div className="flex items-center gap-bg-v2-sm">
          <Icon name={IconName.Layout} size="sm" className="text-fg-quaternary" inheritColor />
          <Text variant="text-xs" weight="medium" className="text-text-secondary">
            Sections to Generate
          </Text>
          <span className="
            inline-flex items-center px-bg-v2-xs py-0.5 rounded-bg-v2-sm
            bg-bg-brand-primary text-text-brand-primary
            text-[11px] font-bg-v2-medium leading-none
          ">
            {selectedCount}/{totalCount}
          </span>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={handleSelectAll}
            disabled={disabled}
            className="
              text-bg-v2-xs font-bg-v2-medium text-text-brand-primary
              hover:underline
              disabled:text-text-disabled disabled:no-underline disabled:cursor-not-allowed
            "
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>

      {/* Section grid */}
      <div className="grid grid-cols-2 gap-x-bg-v2-xl gap-y-1 px-bg-v2-md py-bg-v2-md">
        {CAB_SECTIONS.map((section) => {
          const isChecked = enabledSections.includes(section.key);
          const checkboxId = `cab-section-${section.key}`;

          return (
            <label
              key={section.key}
              className={`
                relative flex items-center gap-bg-v2-sm py-bg-v2-xs rounded-bg-v2-sm
                transition-colors select-none
                ${readOnly ? '' : disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-bg-primary-hover'}
              `}
            >
              <span className={`
                shrink-0 w-4 h-4 rounded-bg-v2-sm border flex items-center justify-center
                transition-colors duration-150
                ${isChecked
                  ? 'bg-bg-brand-solid border-bg-brand-solid'
                  : 'bg-bg-primary border-border-primary'
                }
                ${!readOnly && !disabled ? 'group-hover:border-border-brand' : ''}
              `}>
                {isChecked && (
                  <Icon name={IconName.Check} size="sm" className="text-text-white" inheritColor />
                )}
              </span>
              {!readOnly && (
                <input
                  type="checkbox"
                  id={checkboxId}
                  checked={isChecked}
                  onChange={() => handleToggleSection(section.key)}
                  disabled={disabled}
                  className="sr-only"
                />
              )}
              <Text 
                variant="text-xs" 
                weight={isChecked ? 'medium' : 'regular'} 
                className={isChecked ? 'text-text-primary' : 'text-text-tertiary'}
              >
                {section.label}
              </Text>
            </label>
          );
        })}
      </div>
    </div>
  );
};
